import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Bell, Briefcase, MessageCircle, Globe2, ShieldCheck, Wallet, Settings } from "lucide-react";
import { applications, type Application } from "../../data";
import { BottomNav } from "./ClientNav";

type Cat = "Все" | "Работа" | "Связь" | "Поле" | "Безопасность" | "Финансы";

export default function CatalogPage() {
  const [cat, setCat] = useState<Cat>("Все");
  const [q, setQ] = useState("");

  const items = useMemo(() => {
    let list = applications.filter((a) => a.status === "published");
    if (cat !== "Все") list = list.filter((a) => a.category === cat);
    if (q.trim()) {
      const t = q.toLowerCase();
      list = list.filter((a) => a.name.toLowerCase().includes(t) || a.pkg.toLowerCase().includes(t));
    }
    return list;
  }, [cat, q]);

  return (
    <div className="flex h-full flex-col">
      <Header q={q} onQ={setQ} />

      <div className="flex-1 overflow-y-auto px-4 pb-4">
        {/* Большая карточка обновлений */}
        <UpdatesAlert />

        <Categories cat={cat} onChange={setCat} />

        <ul className="mt-3 flex flex-col gap-1">
          {items.map((a, i) => (
            <li key={a.pkg} style={{ animationDelay: `${Math.min(i, 8) * 35}ms` }} className="stagger-up">
              <AppRow app={a} />
            </li>
          ))}
        </ul>
      </div>

      <BottomNav />
    </div>
  );
}

function Header({ q, onQ }: { q: string; onQ: (v: string) => void }) {
  const navigate = useNavigate();
  const publishedCount = applications.filter((a) => a.status === "published").length;
  return (
    <div className="px-4 pb-2 pt-1">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-[24px] font-semibold tracking-[-0.01em] text-[var(--color-fg)]">Каталог</h1>
          <div className="mt-0.5 text-[11.5px] text-[var(--color-fg-muted)]">
            <span className="font-mono">msk-01</span> · ALL · {publishedCount} приложений
          </div>
        </div>
        <button
          onClick={() => navigate("/client/updates")}
          aria-label="Уведомления"
          className="relative grid h-10 w-10 place-items-center rounded-full bg-[var(--color-surface-2)] text-[var(--color-fg-muted)] hover:bg-[var(--color-surface-3)] hover:text-[var(--color-fg)]"
        >
          <Bell size={18} />
          <span className="absolute right-2 top-2 h-2 w-2 rounded-full bg-[var(--color-danger)]" />
        </button>
      </div>

      <div className="relative mt-3">
        <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-fg-faint)]" />
        <input
          value={q}
          onChange={(e) => onQ(e.target.value)}
          placeholder="Поиск приложений"
          className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-surface-2)] py-2.5 pl-9 pr-3 text-[13px] text-[var(--color-fg)] outline-none focus:border-[var(--color-fg-muted)]"
        />
      </div>
    </div>
  );
}

function UpdatesAlert() {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate("/client/updates")}
      className="mt-2 flex w-full items-center gap-3 rounded-2xl bg-[var(--color-brand)]/8 px-3 py-3 text-left transition-all hover:bg-[var(--color-brand)]/14"
      style={{ background: "rgba(31,58,138,0.07)" }}
    >
      <span
        className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[var(--color-brand)] text-white"
        aria-hidden
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M8 2v8m0 0l3-3m-3 3L5 7m-3 6h12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
      <div className="flex-1">
        <div className="text-[13px] font-medium text-[var(--color-fg)]">12 обновлений доступно</div>
        <div className="text-[11.5px] text-[var(--color-fg-muted)]">Загрузка через msk-01 · 38.4 MB</div>
      </div>
      <span className="shrink-0 rounded-lg bg-[var(--color-brand)] px-3 py-1.5 text-[12px] font-medium text-white">
        Обновить всё
      </span>
    </button>
  );
}

const CATS: { id: Cat; icon: typeof Briefcase }[] = [
  { id: "Работа",       icon: Briefcase },
  { id: "Связь",        icon: MessageCircle },
  { id: "Поле",         icon: Globe2 },
  { id: "Безопасность", icon: ShieldCheck },
  { id: "Финансы",      icon: Wallet },
];

function Categories({ cat, onChange }: { cat: Cat; onChange: (c: Cat) => void }) {
  return (
    <div className="-mx-4 mt-4 overflow-x-auto px-4">
      <div className="flex min-w-max gap-2">
        <Chip active={cat === "Все"} onClick={() => onChange("Все")} icon={Settings}>Все</Chip>
        {CATS.map((c) => (
          <Chip key={c.id} active={cat === c.id} onClick={() => onChange(c.id)} icon={c.icon}>{c.id}</Chip>
        ))}
      </div>
    </div>
  );
}

function Chip({ active, onClick, icon: Icon, children }: { active: boolean; onClick: () => void; icon: typeof Briefcase; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={
        "flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12px] transition-colors " +
        (active
          ? "border-[var(--color-brand)] bg-[var(--color-brand)] text-white"
          : "border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-fg)] hover:bg-[var(--color-surface-2)]")
      }
    >
      <Icon size={13} />
      {children}
    </button>
  );
}

function AppRow({ app }: { app: Application }) {
  const navigate = useNavigate();
  // три состояния: "Обновить" (если есть свежая), "Открыть" (установлено), "Установить"
  // сделаем правдоподобно: docs/mail/vcs/market/security установлены; docs ещё и c обновлением.
  const installed = ["ru.corp.mail", "ru.corp.vcs", "ru.corp.market", "ru.corp.security"].includes(app.pkg);
  const hasUpdate = app.pkg === "ru.corp.docs";

  return (
    <button
      onClick={() => navigate(`/client/app/${app.pkg}`)}
      className="flex w-full items-center gap-3 rounded-xl px-2 py-2.5 text-left transition-colors hover:bg-[var(--color-surface-2)]"
    >
      <span
        className="grid shrink-0 place-items-center rounded-2xl font-semibold text-white"
        style={{ background: app.iconColor, width: 44, height: 44, fontSize: 18 }}
      >
        {app.iconLetter}
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-[14px] font-medium text-[var(--color-fg)]">{app.name}</div>
        <div className="truncate font-mono text-[11px] text-[var(--color-fg-muted)]">
          {app.pkg} · {app.version}{hasUpdate && <span className="text-[var(--color-brand)]"> · обновление 2.1 MB</span>}
        </div>
      </div>
      <RowBtn variant={hasUpdate ? "primary" : installed ? "ghost" : "secondary"}>
        {hasUpdate ? "Обновить" : installed ? "Открыть" : "Установить"}
      </RowBtn>
    </button>
  );
}

function RowBtn({ children, variant }: { children: React.ReactNode; variant: "primary" | "secondary" | "ghost" }) {
  const cls =
    variant === "primary"   ? "bg-[var(--color-brand)] text-white" :
    variant === "secondary" ? "border border-[var(--color-border-strong)] text-[var(--color-fg)]" :
                              "text-[var(--color-fg-muted)]";
  return <span className={"shrink-0 rounded-full px-3 py-1.5 text-[12px] font-medium " + cls}>{children}</span>;
}
